import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '../components';
import { Plus, X, AlertCircle } from 'lucide-react';

import type { IdentifierType } from '../types';
type InvestigationType = 'pessoa' | 'empresa' | 'dominio' | 'email' | 'processo' | 'geral';

interface Identifier {
  id: string;
  type: IdentifierType;
  value: string;
}

export const NewInvestigationPage: React.FC = () => {
  const navigate = useNavigate();
  const [investigationType, setInvestigationType] = useState<InvestigationType>('pessoa');
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [identifiers, setIdentifiers] = useState<Identifier[]>([]);
  const [newIdentifier, setNewIdentifier] = useState<{ type: IdentifierType; value: string }>({
    type: 'nome',
    value: '',
  });
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const identifierTypes: { [key in InvestigationType]: IdentifierType[] } = {
    pessoa: ['cpf', 'nome', 'email', 'telefone'],
    empresa: ['cnpj', 'nome', 'dominio'],
    dominio: ['dominio', 'email'],
    email: ['email', 'nome'],
    processo: ['numero_processo'],
    geral: ['nome', 'email', 'url', 'dominio'],
  };

  const addIdentifier = () => {
    if (!newIdentifier.value.trim()) {
      setError('Preencha o valor do identificador');
      return;
    }

    setIdentifiers([
      ...identifiers,
      {
        id: Date.now().toString(),
        ...newIdentifier,
      },
    ]);

    setNewIdentifier({ type: 'nome', value: '' });
    setError('');
  };

  const removeIdentifier = (id: string) => {
    setIdentifiers(identifiers.filter((i) => i.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Digite um título para a investigação');
      return;
    }

    if (!purpose.trim()) {
      setError('Descreva a finalidade da investigação');
      return;
    }

    if (identifiers.length === 0) {
      setError('Adicione pelo menos um identificador');
      return;
    }

    if (!agreedTerms) {
      setError('Você precisa concordar com os termos');
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Integrar com API real
      const response = await fetch('/api/investigations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          investigation_type: investigationType,
          purpose,
          identifiers: identifiers.map((id) => ({
            type: id.type,
            value: id.value,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Erro ao criar investigação');
      }

      const data = await response.json();
      navigate(`/investigacoes/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Nova Investigação</h1>
        <p className="text-gray-400">
          Inicie uma pesquisa em fontes públicas autorizadas
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tipo de Investigação */}
        <Card>
          <CardHeader>
            <CardTitle>Tipo de Investigação</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(['pessoa', 'empresa', 'dominio', 'email', 'processo', 'geral'] as InvestigationType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setInvestigationType(type)}
                  className={`p-3 rounded-lg border transition-colors capitalize ${
                    investigationType === type
                      ? 'bg-blue-900 border-blue-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Informações da Investigação */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Título da Investigação"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Verificação de CNPJ"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Finalidade da Investigação *
              </label>
              <textarea
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Descreva por que está fazendo esta investigação (conformidade, due diligence, etc)"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                required
              />
              <p className="text-xs text-gray-400 mt-1">
                Obrigatório para fins de rastreabilidade
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Identificadores */}
        <Card>
          <CardHeader>
            <CardTitle>Identificadores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {identifiers.map((id) => (
                <div
                  key={id.id}
                  className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-300 capitalize">{id.type}</p>
                    <p className="text-white">{id.value}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeIdentifier(id.id)}
                    className="p-2 hover:bg-gray-700 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-700 pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">
                    Tipo
                  </label>
                  <select
                    value={newIdentifier.type}
                    onChange={(e) =>
                      setNewIdentifier({
                        ...newIdentifier,
                        type: e.target.value as IdentifierType,
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {identifierTypes[investigationType].map((type) => (
                      <option key={type} value={type} className="capitalize">
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  value={newIdentifier.value}
                  onChange={(e) =>
                    setNewIdentifier({
                      ...newIdentifier,
                      value: e.target.value,
                    })
                  }
                  placeholder="Valor"
                />
              </div>

              <Button
                type="button"
                variant="secondary"
                onClick={addIdentifier}
                className="w-full flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Adicionar Identificador
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Termos */}
        <Card>
          <CardContent className="py-6 space-y-4">
            <div className="flex gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="terms" className="text-sm text-gray-300">
                Confirmo que possuo autorização legal ou base legítima para realizar esta
                investigação e que os dados serão utilizados conforme a finalidade registrada.
              </label>
            </div>

            <div className="p-3 bg-amber-900 border border-amber-700 rounded-lg flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-100">
                <p className="font-medium mb-1">Aviso Legal</p>
                <p>
                  Esta plataforma respeita a LGPD. Dados pessoais só podem ser processados com
                  base legal apropriada. Uso indevido é responsabilidade do usuário.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Erro */}
        {error && (
          <div className="p-3 bg-red-900 border border-red-700 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Ações */}
        <div className="flex gap-3">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            disabled={!agreedTerms}
            className="flex-1"
          >
            Iniciar Investigação
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => navigate('/investigacoes')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
};
